import * as React from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    TableSortLabel,
    TablePagination,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import {
    useDataProvider,
    useGetIdentity,
    useGetList,
    useNotify,
    useRedirect,
    useUpdate,
    Loading,
} from 'react-admin';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import type { Identifier } from 'react-admin';
import type { Sale } from '../types';
import { useSiteStatus } from '../root/SiteStatusContext';
import { supabase } from '../providers/supabase/supabase';
import AdminBugReportsTable from '../bugs/AdminBugReportsTable';

const HPT_STATUS_OPTIONS = ['Live', 'System Update', 'Beta'] as const;
export type HptStatus = (typeof HPT_STATUS_OPTIONS)[number];

export interface SiteStatusLogEntry {
    id: string;
    originalStatus: HptStatus;
    newStatus: HptStatus;
    userName: string;
    userId: string;
    datetime: string;
    message: string;
    code?: string;
}

function generateBetaCode(): string {
    const chars =
        'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    return Array.from(
        { length: 8 },
        () => chars[Math.floor(Math.random() * chars.length)]
    ).join('');
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`admin-panel-tabpanel-${index}`}
            aria-labelledby={`admin-panel-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

function HptStatusChip({ status }: { status: HptStatus }) {
    const chipSx = React.useMemo(() => {
        switch (status) {
            case 'Live':
                return {
                    bgcolor: '#d4edda',
                    color: '#155724',
                };
            case 'System Update':
                return {
                    bgcolor: '#f8d7da',
                    color: '#721c24',
                };
            case 'Beta':
                return {
                    bgcolor: '#fff3cd',
                    color: '#856404',
                };
            default:
                return {};
        }
    }, [status]);
    return <Chip label={status} size="small" sx={chipSx} />;
}

function UserStatusChips({ record }: { record: Sale }) {
    return (
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {record.administrator && (
                <Chip
                    label="Admin"
                    size="small"
                    variant="outlined"
                    color="primary"
                />
            )}
            {record.disabled && (
                <Chip
                    label="Disabled"
                    size="small"
                    variant="outlined"
                    color="warning"
                />
            )}
            {record.approved === false && (
                <Chip
                    label="Pending"
                    size="small"
                    variant="outlined"
                    color="error"
                />
            )}
            {record.approved === true && (
                <Chip
                    label="Approved"
                    size="small"
                    variant="outlined"
                    color="success"
                />
            )}
            {record.approved !== true && record.approved !== false && (
                <Chip label="Unknown" size="small" variant="outlined" />
            )}
        </Stack>
    );
}

function UserRowActions({
    user,
    onApprove,
    onDisable,
    onEnable,
    onDelete,
    isPending,
}: {
    user: Sale;
    onApprove: () => void;
    onDisable: () => void;
    onEnable: () => void;
    onDelete: () => void;
    isPending: boolean;
}) {
    const canApprove = user.approved === false;
    const canDisable = !user.disabled;
    const canEnable = user.disabled === true;
    const canDelete = user.disabled === true;

    return (
        <Stack direction="row" flexWrap="wrap" gap={0.5} alignItems="center">
            {canApprove && (
                <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    onClick={onApprove}
                    disabled={isPending}
                >
                    Approve
                </Button>
            )}
            {canDisable && (
                <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    onClick={onDisable}
                    disabled={isPending}
                >
                    Disable
                </Button>
            )}
            {canEnable && (
                <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={onEnable}
                    disabled={isPending}
                >
                    Enable
                </Button>
            )}
            {canDelete && (
                <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={onDelete}
                    disabled={isPending}
                >
                    Delete
                </Button>
            )}
            {!canApprove && !canDisable && !canEnable && !canDelete && (
                <Typography variant="body2" color="text.secondary">
                    —
                </Typography>
            )}
        </Stack>
    );
}

function AdminUsersTable() {
    const notify = useNotify();
    const dataProvider = useDataProvider();
    const [update, { isPending }] = useUpdate();
    const [selectedIds, setSelectedIds] = React.useState<Identifier[]>([]);
    const [isBulkPending, setIsBulkPending] = React.useState(false);
    const [orderBy, setOrderBy] = React.useState<'name' | 'email' | 'status'>(
        'name'
    );
    const [order, setOrder] = React.useState<'asc' | 'desc'>('asc');
    const [statusFilter, setStatusFilter] = React.useState<
        'all' | 'admin' | 'approved' | 'pending' | 'disabled'
    >('all');
    const [searchQuery, setSearchQuery] = React.useState('');
    const {
        data: users,
        isLoading,
        error,
        refetch,
    } = useGetList<Sale>('sales', {
        pagination: { page: 1, perPage: 500 },
        sort: { field: 'first_name', order: 'ASC' },
    });

    const handleSort = React.useCallback(
        (column: 'name' | 'email' | 'status') => {
            if (orderBy === column) {
                setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
            } else {
                setOrderBy(column);
                setOrder('asc');
            }
        },
        [orderBy]
    );

    const getUserStatusKey = React.useCallback((u: Sale): string => {
        if (u.disabled) return 'disabled';
        if (u.administrator) return 'admin';
        if (u.approved === false) return 'pending';
        if (u.approved === true) return 'approved';
        return 'unknown';
    }, []);

    const list = React.useMemo(() => {
        let filtered = users ?? [];

        // Search filter
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase().trim();
            filtered = filtered.filter(
                u =>
                    `${u.first_name} ${u.last_name}`
                        .toLowerCase()
                        .includes(q) ||
                    (u.email?.toLowerCase().includes(q) ?? false)
            );
        }

        // Status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(
                u => getUserStatusKey(u) === statusFilter
            );
        }

        // Sort
        const sorted = [...filtered].sort((a, b) => {
            let cmp = 0;
            switch (orderBy) {
                case 'name':
                    cmp = `${a.first_name} ${a.last_name}`.localeCompare(
                        `${b.first_name} ${b.last_name}`
                    );
                    break;
                case 'email':
                    cmp = (a.email ?? '').localeCompare(b.email ?? '');
                    break;
                case 'status':
                    cmp = getUserStatusKey(a).localeCompare(
                        getUserStatusKey(b)
                    );
                    break;
            }
            return order === 'asc' ? cmp : -cmp;
        });

        return sorted;
    }, [users, searchQuery, statusFilter, orderBy, order, getUserStatusKey]);
    const selectedSet = React.useMemo(
        () => new Set(selectedIds.map(String)),
        [selectedIds]
    );
    const selectedUsers = React.useMemo(
        () => list.filter(u => selectedSet.has(String(u.id))),
        [list, selectedSet]
    );
    const someSelected = selectedIds.length > 0;
    const allSelected = list.length > 0 && selectedIds.length === list.length;
    const someIndeterminate = someSelected && !allSelected;

    const handleSelectAll = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.checked) {
                setSelectedIds(list.map(u => u.id));
            } else {
                setSelectedIds([]);
            }
        },
        [list]
    );

    const handleSelectOne = React.useCallback((id: Identifier) => {
        setSelectedIds(prev => {
            const next = new Set(prev.map(String));
            const key = String(id);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return Array.from(next);
        });
    }, []);

    const handleBulkApprove = React.useCallback(async () => {
        const toApprove = selectedUsers.filter(u => u.approved === false);
        if (toApprove.length === 0) {
            notify('No pending users selected', { type: 'info' });
            return;
        }
        setIsBulkPending(true);
        try {
            await Promise.all(
                toApprove.map(user =>
                    dataProvider.update('sales', {
                        id: user.id,
                        data: { approved: true },
                        previousData: user,
                    })
                )
            );
            notify(`${toApprove.length} user(s) approved`, { type: 'success' });
            setSelectedIds([]);
            refetch();
        } catch (e: unknown) {
            const msg =
                e instanceof Error ? e.message : 'Failed to approve selected';
            notify(msg, { type: 'error' });
        } finally {
            setIsBulkPending(false);
        }
    }, [selectedUsers, dataProvider, notify, refetch]);

    const handleBulkDisable = React.useCallback(async () => {
        const toDisable = selectedUsers.filter(u => !u.disabled);
        if (toDisable.length === 0) {
            notify('No enabled users selected', { type: 'info' });
            return;
        }
        setIsBulkPending(true);
        try {
            await Promise.all(
                toDisable.map(user =>
                    dataProvider.update('sales', {
                        id: user.id,
                        data: { disabled: true },
                        previousData: user,
                    })
                )
            );
            notify(`${toDisable.length} user(s) disabled`, { type: 'success' });
            setSelectedIds([]);
            refetch();
        } catch (e: unknown) {
            const msg =
                e instanceof Error ? e.message : 'Failed to disable selected';
            notify(msg, { type: 'error' });
        } finally {
            setIsBulkPending(false);
        }
    }, [selectedUsers, dataProvider, notify, refetch]);

    const handleApprove = React.useCallback(
        (user: Sale) => {
            update(
                'sales',
                {
                    id: user.id,
                    data: { approved: true },
                    previousData: user,
                },
                {
                    onSuccess: () => {
                        notify('User approved', { type: 'success' });
                        refetch();
                    },
                    onError: (e: Error) => {
                        notify(e.message ?? 'Failed to approve user', {
                            type: 'error',
                        });
                    },
                }
            );
        },
        [update, notify, refetch]
    );

    const handleDisable = React.useCallback(
        (user: Sale) => {
            update(
                'sales',
                {
                    id: user.id,
                    data: { disabled: true },
                    previousData: user,
                },
                {
                    onSuccess: () => {
                        notify('User disabled', { type: 'success' });
                        refetch();
                    },
                    onError: (e: Error) => {
                        notify(e.message ?? 'Failed to disable user', {
                            type: 'error',
                        });
                    },
                }
            );
        },
        [update, notify, refetch]
    );

    const handleEnable = React.useCallback(
        (user: Sale) => {
            update(
                'sales',
                {
                    id: user.id,
                    data: { disabled: false },
                    previousData: user,
                },
                {
                    onSuccess: () => {
                        notify('User enabled', { type: 'success' });
                        refetch();
                    },
                    onError: (e: Error) => {
                        notify(e.message ?? 'Failed to enable user', {
                            type: 'error',
                        });
                    },
                }
            );
        },
        [update, notify, refetch]
    );

    const [deleteTarget, setDeleteTarget] = React.useState<Sale | null>(null);
    const [isDeleting, setIsDeleting] = React.useState(false);

    const handleDeleteConfirm = React.useCallback(async () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        try {
            const { data: sessionData } = await supabase.auth.getSession();
            const token = sessionData?.session?.access_token;
            const response = await fetch(
                `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/users`,
                {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ sales_id: deleteTarget.id }),
                }
            );
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(
                    err.error ?? `Delete failed (${response.status})`
                );
            }
            notify('User deleted', { type: 'success' });
            setDeleteTarget(null);
            setSelectedIds([]);
            refetch();
        } catch (e: unknown) {
            const msg =
                e instanceof Error ? e.message : 'Failed to delete user';
            notify(msg, { type: 'error' });
        } finally {
            setIsDeleting(false);
        }
    }, [deleteTarget, notify, refetch]);

    if (isLoading) {
        return <Loading />;
    }
    if (error) {
        return (
            <Typography color="error">
                Failed to load users: {error.message}
            </Typography>
        );
    }

    const bulkPending = isPending || isBulkPending;

    return (
        <Stack spacing={2} sx={{ width: '100%' }}>
            <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ flexWrap: 'wrap', gap: 1 }}
            >
                <TextField
                    size="small"
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    sx={{ minWidth: 220 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel id="user-status-filter-label">
                        Status
                    </InputLabel>
                    <Select
                        labelId="user-status-filter-label"
                        value={statusFilter}
                        label="Status"
                        onChange={e =>
                            setStatusFilter(
                                e.target.value as typeof statusFilter
                            )
                        }
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="admin">Admin</MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="disabled">Disabled</MenuItem>
                    </Select>
                </FormControl>
                {someSelected && (
                    <>
                        <Typography variant="body2" color="text.secondary">
                            {selectedIds.length} selected
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            onClick={handleBulkApprove}
                            disabled={bulkPending}
                        >
                            Approve Selected
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={handleBulkDisable}
                            disabled={bulkPending}
                        >
                            Disable Selected
                        </Button>
                    </>
                )}
            </Stack>
            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ width: '100%' }}
            >
                <Table size="medium" aria-label="Users in the system">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    indeterminate={someIndeterminate}
                                    checked={allSelected}
                                    onChange={handleSelectAll}
                                    disabled={list.length === 0}
                                    inputProps={{
                                        'aria-label': 'Select all users',
                                    }}
                                />
                            </TableCell>
                            <TableCell
                                sx={{ fontWeight: 600 }}
                                sortDirection={
                                    orderBy === 'name' ? order : false
                                }
                            >
                                <TableSortLabel
                                    active={orderBy === 'name'}
                                    direction={
                                        orderBy === 'name' ? order : 'asc'
                                    }
                                    onClick={() => handleSort('name')}
                                >
                                    Name
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                sx={{ fontWeight: 600 }}
                                sortDirection={
                                    orderBy === 'email' ? order : false
                                }
                            >
                                <TableSortLabel
                                    active={orderBy === 'email'}
                                    direction={
                                        orderBy === 'email' ? order : 'asc'
                                    }
                                    onClick={() => handleSort('email')}
                                >
                                    Email
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                sx={{ fontWeight: 600 }}
                                sortDirection={
                                    orderBy === 'status' ? order : false
                                }
                            >
                                <TableSortLabel
                                    active={orderBy === 'status'}
                                    direction={
                                        orderBy === 'status' ? order : 'asc'
                                    }
                                    onClick={() => handleSort('status')}
                                >
                                    Status
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {list.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <Typography color="text.secondary">
                                        No users found.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            list.map(user => (
                                <TableRow key={user.id}>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedSet.has(
                                                String(user.id)
                                            )}
                                            onChange={() =>
                                                handleSelectOne(user.id)
                                            }
                                            inputProps={{
                                                'aria-label': `Select ${user.first_name} ${user.last_name}`,
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {user.first_name} {user.last_name}
                                    </TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>
                                        <UserStatusChips record={user} />
                                    </TableCell>
                                    <TableCell>
                                        <UserRowActions
                                            user={user}
                                            onApprove={() =>
                                                handleApprove(user)
                                            }
                                            onDisable={() =>
                                                handleDisable(user)
                                            }
                                            onEnable={() => handleEnable(user)}
                                            onDelete={() =>
                                                setDeleteTarget(user)
                                            }
                                            isPending={bulkPending}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Dialog
                open={deleteTarget !== null}
                onClose={() => !isDeleting && setDeleteTarget(null)}
            >
                <DialogTitle>Delete User</DialogTitle>
                <DialogContent>
                    <Typography>
                        Permanently delete{' '}
                        <strong>
                            {deleteTarget?.first_name} {deleteTarget?.last_name}
                        </strong>{' '}
                        ({deleteTarget?.email})? This removes their account and
                        all associated data. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDeleteTarget(null)}
                        disabled={isDeleting}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        disabled={isDeleting}
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}

export const AdminPanelPage = () => {
    const { siteStatus: globalSiteStatus, setSiteStatus: setGlobalSiteStatus } =
        useSiteStatus();
    const [tabValue, setTabValue] = React.useState(() => {
        const hash = window.location.hash;
        const qIndex = hash.indexOf('?');
        if (qIndex !== -1) {
            const params = new URLSearchParams(hash.slice(qIndex));
            const tab = params.get('tab');
            if (tab === 'bugs') return 3;
            if (tab === 'users') return 1;
            if (tab === 'misc') return 2;
        }
        return 0;
    });
    const [hptStatus, setHptStatus] =
        React.useState<HptStatus>(globalSiteStatus);
    const [savedHptStatus, setSavedHptStatus] =
        React.useState<HptStatus>(globalSiteStatus);
    const [statusLogSort, setStatusLogSort] = React.useState<{
        column: 'status' | 'user' | 'datetime' | 'message' | 'code';
        direction: 'asc' | 'desc';
    }>({ column: 'datetime', direction: 'desc' });
    const [statusLogPage, setStatusLogPage] = React.useState(0);
    const [statusLogRowsPerPage, setStatusLogRowsPerPage] = React.useState(5);
    const [messageDialogOpen, setMessageDialogOpen] = React.useState(false);
    const [systemUpdateMessage, setSystemUpdateMessage] = React.useState('');
    const [systemUpdateCode, setSystemUpdateCode] = React.useState('');
    const [codeDialogOpen, setCodeDialogOpen] = React.useState(false);
    const [betaCode, setBetaCode] = React.useState('');
    const { identity, isLoading, error } = useGetIdentity();
    const redirect = useRedirect();
    const notify = useNotify();
    const dataProvider = useDataProvider();

    const {
        data: siteStatusLogRaw,
        isLoading: logLoading,
        error: logError,
        refetch: refetchLog,
    } = useGetList('site_status_log', {
        pagination: { page: 1, perPage: 500 },
        sort: { field: 'created_at', order: 'DESC' },
    });

    const siteStatusLog = React.useMemo(() => {
        if (!siteStatusLogRaw) return [];
        return siteStatusLogRaw.map((row: Record<string, unknown>) => ({
            id: row.id,
            originalStatus: row.original_status as HptStatus,
            newStatus: row.new_status as HptStatus,
            userName: String(row.user_name ?? ''),
            userId: String(row.user_id ?? ''),
            datetime: row.created_at as string,
            message: String(row.message ?? ''),
            code: row.code != null ? String(row.code) : undefined,
        })) as SiteStatusLogEntry[];
    }, [siteStatusLogRaw]);

    React.useEffect(() => {
        if (!isLoading && !error && identity && !identity.administrator) {
            redirect('/');
        }
    }, [isLoading, error, identity, redirect]);

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const addLogEntry = React.useCallback(
        async (
            originalStatus: HptStatus,
            newStatus: HptStatus,
            message: string,
            code?: string
        ) => {
            if (!identity) return;
            try {
                await dataProvider.create('site_status_log', {
                    data: {
                        original_status: originalStatus,
                        new_status: newStatus,
                        user_name:
                            identity.fullName ?? identity.email ?? 'Unknown',
                        user_id: String(identity.id),
                        message: message || null,
                        code: code || null,
                    },
                });
                refetchLog();
            } catch (e) {
                console.error('Failed to add site status log entry:', e);
                notify('Failed to save log entry', { type: 'error' });
            }
        },
        [identity, dataProvider, refetchLog, notify]
    );

    const updateSiteStatusInSupabase = React.useCallback(
        async (value: HptStatus, message: string, code: string | undefined) => {
            if (!identity) return;
            const { error } = await supabase
                .from('site_status')
                .update({
                    value,
                    updated_at: new Date().toISOString(),
                    updated_by_user_id: String(identity.id),
                    updated_by_user_name:
                        identity.fullName ?? identity.email ?? 'Unknown',
                    message: message || null,
                    code: code || null,
                })
                .eq('key', 'current');
            if (error) {
                console.error(
                    '[AdminPanel] Failed to update site_status:',
                    error
                );
                notify('Failed to update site status', { type: 'error' });
            }
        },
        [identity, notify]
    );

    const hptStatusDirty = hptStatus !== savedHptStatus;
    const handleSaveHptStatus = () => {
        if (hptStatus === 'System Update') {
            setSystemUpdateCode(generateBetaCode());
            setMessageDialogOpen(true);
            return;
        }
        if (hptStatus === 'Beta') {
            setBetaCode(generateBetaCode());
            setCodeDialogOpen(true);
            return;
        }
        setSavedHptStatus(hptStatus);
        setGlobalSiteStatus(hptStatus);
        updateSiteStatusInSupabase(hptStatus, '', undefined);
        addLogEntry(savedHptStatus, hptStatus, '');
        notify('HPT Status saved', { type: 'success' });
    };

    const handleMessageDialogClose = () => {
        setMessageDialogOpen(false);
        setSystemUpdateMessage('');
        setSystemUpdateCode('');
    };

    const handleMessageDialogSave = async () => {
        setSavedHptStatus('System Update');
        setGlobalSiteStatus('System Update');
        await updateSiteStatusInSupabase(
            'System Update',
            systemUpdateMessage.trim(),
            systemUpdateCode.trim() || undefined
        );
        addLogEntry(
            savedHptStatus,
            'System Update',
            systemUpdateMessage.trim(),
            systemUpdateCode.trim() || undefined
        );
        handleMessageDialogClose();
        notify('HPT Status saved', { type: 'success' });
    };

    const handleCodeDialogClose = () => {
        setCodeDialogOpen(false);
        setBetaCode('');
    };

    const handleCodeDialogSave = async () => {
        setSavedHptStatus('Beta');
        setGlobalSiteStatus('Beta');
        await updateSiteStatusInSupabase(
            'Beta',
            '',
            betaCode.trim() || undefined
        );
        addLogEntry(savedHptStatus, 'Beta', '', betaCode.trim() || undefined);
        handleCodeDialogClose();
        notify('HPT Status saved', { type: 'success' });
    };

    const handleStatusLogSort = (column: typeof statusLogSort.column) => {
        setStatusLogSort(prev => ({
            column,
            direction:
                prev.column === column && prev.direction === 'asc'
                    ? 'desc'
                    : 'asc',
        }));
    };

    const sortedStatusLog = React.useMemo(() => {
        const { column, direction } = statusLogSort;
        const mult = direction === 'asc' ? 1 : -1;
        return [...siteStatusLog].sort((a, b) => {
            let aVal: string | number;
            let bVal: string | number;
            switch (column) {
                case 'status':
                    aVal = `${a.originalStatus} > ${a.newStatus}`;
                    bVal = `${b.originalStatus} > ${b.newStatus}`;
                    break;
                case 'user':
                    aVal = a.userName;
                    bVal = b.userName;
                    break;
                case 'datetime':
                    aVal = new Date(a.datetime).getTime();
                    bVal = new Date(b.datetime).getTime();
                    break;
                case 'message':
                    aVal = a.message;
                    bVal = b.message;
                    break;
                case 'code':
                    aVal = a.code ?? '';
                    bVal = b.code ?? '';
                    break;
                default:
                    return 0;
            }
            if (typeof aVal === 'string' && typeof bVal === 'string') {
                return mult * aVal.localeCompare(bVal);
            }
            return mult * (Number(aVal) - Number(bVal));
        });
    }, [siteStatusLog, statusLogSort]);

    const paginatedStatusLog = React.useMemo(() => {
        if (statusLogRowsPerPage === -1) {
            return sortedStatusLog;
        }
        const start = statusLogPage * statusLogRowsPerPage;
        return sortedStatusLog.slice(start, start + statusLogRowsPerPage);
    }, [sortedStatusLog, statusLogPage, statusLogRowsPerPage]);

    const handleStatusLogPageChange = (_: unknown, newPage: number) => {
        setStatusLogPage(newPage);
    };

    const handleStatusLogRowsPerPageChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const value = e.target.value;
        const num = value === 'all' || value === '-1' ? -1 : Number(value);
        setStatusLogRowsPerPage(num);
        setStatusLogPage(0);
    };

    if (isLoading) {
        return <Loading />;
    }

    if (error) {
        console.error('Error fetching identity:', error);
        redirect('/login');
        return null;
    }

    if (!identity?.administrator) {
        return <Loading />;
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Card sx={{ width: '100%' }}>
                <CardContent sx={{ px: 0, '&:last-child': { pb: 0 } }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 3,
                            pb: 2,
                        }}
                    >
                        <AdminPanelSettingsOutlinedIcon
                            color="primary"
                            sx={{ fontSize: 32 }}
                        />
                        <Typography variant="h5" component="h1">
                            Admin Panel
                        </Typography>
                    </Box>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        aria-label="Admin panel tabs"
                        sx={{
                            px: 3,
                            borderBottom: 1,
                            borderColor: 'divider',
                        }}
                    >
                        <Tab
                            icon={<StorageOutlinedIcon />}
                            iconPosition="start"
                            label="System Status"
                            id="admin-panel-tab-0"
                            aria-controls="admin-panel-tabpanel-0"
                        />
                        <Tab
                            icon={<PeopleOutlineOutlinedIcon />}
                            iconPosition="start"
                            label="Users"
                            id="admin-panel-tab-1"
                            aria-controls="admin-panel-tabpanel-1"
                        />
                        <Tab
                            icon={<MoreHorizIcon />}
                            iconPosition="start"
                            label="Misc"
                            id="admin-panel-tab-2"
                            aria-controls="admin-panel-tabpanel-2"
                        />
                        <Tab
                            icon={<BugReportOutlinedIcon />}
                            iconPosition="start"
                            label="Bugs"
                            id="admin-panel-tab-3"
                            aria-controls="admin-panel-tabpanel-3"
                        />
                    </Tabs>
                    <Box sx={{ px: 3 }}>
                        <TabPanel value={tabValue} index={0}>
                            <Stack spacing={3}>
                                <FormControl
                                    sx={{ minWidth: 220 }}
                                    size="medium"
                                >
                                    <InputLabel id="hpt-status-label">
                                        HPT Status
                                    </InputLabel>
                                    <Select
                                        labelId="hpt-status-label"
                                        id="hpt-status"
                                        value={hptStatus}
                                        label="HPT Status"
                                        onChange={e =>
                                            setHptStatus(
                                                e.target.value as HptStatus
                                            )
                                        }
                                    >
                                        {HPT_STATUS_OPTIONS.map(opt => (
                                            <MenuItem key={opt} value={opt}>
                                                {opt}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {hptStatusDirty && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSaveHptStatus}
                                    >
                                        Save
                                    </Button>
                                )}
                                <Card variant="outlined" sx={{ width: '100%' }}>
                                    <CardContent>
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={600}
                                            sx={{ mb: 2 }}
                                        >
                                            Site Status Log
                                        </Typography>
                                        {logError && (
                                            <Typography
                                                color="error"
                                                variant="body2"
                                                sx={{ mb: 2 }}
                                            >
                                                Failed to load log:{' '}
                                                {(logError as Error).message}
                                            </Typography>
                                        )}
                                        <TableContainer
                                            component={Paper}
                                            variant="outlined"
                                            sx={{ width: '100%' }}
                                        >
                                            <Table
                                                size="small"
                                                aria-label="Site Status Log"
                                            >
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell
                                                            sx={{
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            <TableSortLabel
                                                                active={
                                                                    statusLogSort.column ===
                                                                    'status'
                                                                }
                                                                direction={
                                                                    statusLogSort.column ===
                                                                    'status'
                                                                        ? statusLogSort.direction
                                                                        : 'asc'
                                                                }
                                                                onClick={() =>
                                                                    handleStatusLogSort(
                                                                        'status'
                                                                    )
                                                                }
                                                            >
                                                                Status
                                                            </TableSortLabel>
                                                        </TableCell>
                                                        <TableCell
                                                            sx={{
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            <TableSortLabel
                                                                active={
                                                                    statusLogSort.column ===
                                                                    'user'
                                                                }
                                                                direction={
                                                                    statusLogSort.column ===
                                                                    'user'
                                                                        ? statusLogSort.direction
                                                                        : 'asc'
                                                                }
                                                                onClick={() =>
                                                                    handleStatusLogSort(
                                                                        'user'
                                                                    )
                                                                }
                                                            >
                                                                User
                                                            </TableSortLabel>
                                                        </TableCell>
                                                        <TableCell
                                                            sx={{
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            <TableSortLabel
                                                                active={
                                                                    statusLogSort.column ===
                                                                    'datetime'
                                                                }
                                                                direction={
                                                                    statusLogSort.column ===
                                                                    'datetime'
                                                                        ? statusLogSort.direction
                                                                        : 'asc'
                                                                }
                                                                onClick={() =>
                                                                    handleStatusLogSort(
                                                                        'datetime'
                                                                    )
                                                                }
                                                            >
                                                                Date & Time
                                                            </TableSortLabel>
                                                        </TableCell>
                                                        <TableCell
                                                            sx={{
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            <TableSortLabel
                                                                active={
                                                                    statusLogSort.column ===
                                                                    'message'
                                                                }
                                                                direction={
                                                                    statusLogSort.column ===
                                                                    'message'
                                                                        ? statusLogSort.direction
                                                                        : 'asc'
                                                                }
                                                                onClick={() =>
                                                                    handleStatusLogSort(
                                                                        'message'
                                                                    )
                                                                }
                                                            >
                                                                Message
                                                            </TableSortLabel>
                                                        </TableCell>
                                                        <TableCell
                                                            sx={{
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            <TableSortLabel
                                                                active={
                                                                    statusLogSort.column ===
                                                                    'code'
                                                                }
                                                                direction={
                                                                    statusLogSort.column ===
                                                                    'code'
                                                                        ? statusLogSort.direction
                                                                        : 'asc'
                                                                }
                                                                onClick={() =>
                                                                    handleStatusLogSort(
                                                                        'code'
                                                                    )
                                                                }
                                                            >
                                                                Code
                                                            </TableSortLabel>
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {logLoading &&
                                                    siteStatusLog.length ===
                                                        0 ? (
                                                        <TableRow>
                                                            <TableCell
                                                                colSpan={5}
                                                                align="center"
                                                            >
                                                                <Typography
                                                                    color="text.secondary"
                                                                    variant="body2"
                                                                >
                                                                    Loading…
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : paginatedStatusLog.length ===
                                                      0 ? (
                                                        <TableRow>
                                                            <TableCell
                                                                colSpan={5}
                                                                align="center"
                                                            >
                                                                <Typography
                                                                    color="text.secondary"
                                                                    variant="body2"
                                                                >
                                                                    No status
                                                                    changes yet.
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : (
                                                        paginatedStatusLog.map(
                                                            entry => (
                                                                <TableRow
                                                                    key={
                                                                        entry.id
                                                                    }
                                                                >
                                                                    <TableCell>
                                                                        <Stack
                                                                            direction="row"
                                                                            spacing={
                                                                                0.5
                                                                            }
                                                                            alignItems="center"
                                                                            flexWrap="wrap"
                                                                            component="span"
                                                                        >
                                                                            <HptStatusChip
                                                                                status={
                                                                                    entry.originalStatus
                                                                                }
                                                                            />
                                                                            <Typography
                                                                                component="span"
                                                                                color="text.secondary"
                                                                            >
                                                                                &gt;
                                                                            </Typography>
                                                                            <HptStatusChip
                                                                                status={
                                                                                    entry.newStatus
                                                                                }
                                                                            />
                                                                        </Stack>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {
                                                                            entry.userName
                                                                        }
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {new Date(
                                                                            entry.datetime
                                                                        ).toLocaleString()}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {entry.message ||
                                                                            '—'}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {entry.code ||
                                                                            '—'}
                                                                    </TableCell>
                                                                </TableRow>
                                                            )
                                                        )
                                                    )}
                                                </TableBody>
                                                <TableFooter>
                                                    <TableRow>
                                                        <TableCell
                                                            colSpan={5}
                                                            padding="none"
                                                        >
                                                            <TablePagination
                                                                count={
                                                                    sortedStatusLog.length
                                                                }
                                                                page={
                                                                    statusLogPage
                                                                }
                                                                onPageChange={
                                                                    handleStatusLogPageChange
                                                                }
                                                                rowsPerPage={
                                                                    statusLogRowsPerPage ===
                                                                    -1
                                                                        ? Math.max(
                                                                              sortedStatusLog.length,
                                                                              1
                                                                          )
                                                                        : statusLogRowsPerPage
                                                                }
                                                                onRowsPerPageChange={
                                                                    handleStatusLogRowsPerPageChange
                                                                }
                                                                rowsPerPageOptions={[
                                                                    5,
                                                                    10,
                                                                    15,
                                                                    {
                                                                        value: -1,
                                                                        label: 'All',
                                                                    },
                                                                ]}
                                                                labelRowsPerPage="Rows per page:"
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                </TableFooter>
                                            </Table>
                                        </TableContainer>
                                    </CardContent>
                                </Card>
                            </Stack>
                        </TabPanel>
                        <TabPanel value={tabValue} index={1}>
                            <AdminUsersTable />
                        </TabPanel>
                        <TabPanel value={tabValue} index={2}>
                            <Typography color="text.secondary">
                                Misc — other admin options.
                            </Typography>
                        </TabPanel>
                        <TabPanel value={tabValue} index={3}>
                            <AdminBugReportsTable />
                        </TabPanel>
                    </Box>
                </CardContent>
            </Card>
            <Dialog
                open={messageDialogOpen}
                onClose={handleMessageDialogClose}
                maxWidth="sm"
                fullWidth
                aria-labelledby="message-dialog-title"
            >
                <DialogTitle id="message-dialog-title">
                    Message to Users
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="system-update-message"
                            label="Message to Users"
                            fullWidth
                            multiline
                            minRows={3}
                            maxRows={8}
                            value={systemUpdateMessage}
                            onChange={e =>
                                setSystemUpdateMessage(e.target.value)
                            }
                            placeholder="Enter a message to display to users during the system update..."
                        />
                        <TextField
                            margin="dense"
                            id="system-update-code"
                            label="Code"
                            fullWidth
                            value={systemUpdateCode}
                            onChange={e => setSystemUpdateCode(e.target.value)}
                            placeholder="8-character code"
                        />
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                                setSystemUpdateCode(generateBetaCode())
                            }
                        >
                            Generate new code
                        </Button>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleMessageDialogClose}>Cancel</Button>
                    <Button
                        onClick={handleMessageDialogSave}
                        variant="contained"
                        color="primary"
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={codeDialogOpen}
                onClose={handleCodeDialogClose}
                maxWidth="sm"
                fullWidth
                aria-labelledby="code-dialog-title"
            >
                <DialogTitle id="code-dialog-title">Code</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="beta-code"
                            label="Code"
                            fullWidth
                            value={betaCode}
                            onChange={e => setBetaCode(e.target.value)}
                            placeholder="8-character code"
                        />
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setBetaCode(generateBetaCode())}
                        >
                            Generate new code
                        </Button>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCodeDialogClose}>Cancel</Button>
                    <Button
                        onClick={handleCodeDialogSave}
                        variant="contained"
                        color="primary"
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
